import { useMemo, useRef, useState, type ReactNode } from 'react'
import {
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  Check,
  CircleX,
  Download,
  FileSpreadsheet,
  Lock,
  RefreshCw,
  SkipForward,
  Upload,
  UserPlus,
} from 'lucide-react'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { StatCard } from '@/components/shared/stat-card'
import { EmptyState, Loading } from '@/components/shared/feedback'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge, type BadgeProps } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/misc'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { apiError } from '@/lib/api-client'
import { i18n } from '@/lib/i18n'
import { cn, formatNumber } from '@/lib/utils'
import { useCdpProperties } from '@/hooks/use-cdp'
import { useMyPermissions, useTags, useTeam } from '@/hooks/use-settings'
import {
  useContactImport,
  useContactImportPreview,
  type ContactImportOptions,
  type ContactImportResponse,
  type ImportRowResult,
  type ImportRowStatus,
} from '@/hooks/use-contacts'
import {
  BUILTIN_FIELDS,
  MAX_IMPORT_ROWS,
  buildImportRows,
  downloadCsv,
  guessMapping,
  hasIdentityColumn,
  readSpreadsheetFile,
  templateCsv,
  toCsv,
  type SheetData,
  type TargetField,
} from './contact-import-lib'

const STEPS = ['upload', 'mapping', 'options', 'preview', 'result'] as const
const SKIP = '__skip__'
const NOBODY = '__nobody__'

const STATUS_META: Record<ImportRowStatus, { label: string; preview: string; variant: BadgeProps['variant'] }> = {
  create: { get label() { return i18n.t('custList:import.status.created') }, get preview() { return i18n.t('custList:import.status.willCreate') }, variant: 'success' },
  update: { get label() { return i18n.t('custList:import.status.updated') }, get preview() { return i18n.t('custList:import.status.willUpdate') }, variant: 'default' },
  skip: { get label() { return i18n.t('custList:import.status.skipped') }, get preview() { return i18n.t('custList:import.status.skipped') }, variant: 'secondary' },
  error: { get label() { return i18n.t('custList:import.status.error') }, get preview() { return i18n.t('custList:import.status.error') }, variant: 'destructive' },
}

const DEFAULT_OPTIONS: ContactImportOptions = {
  dedupeByPhone: true,
  dedupeByEmail: true,
  onDuplicate: 'skip',
  extraTag: '',
  assignedUserId: undefined,
  runAutomation: true,
}

/**
 * Nhập khách hàng hàng loạt từ CSV / Excel (mẫu SaleSmartly) — hộp thoại mở từ nút "Nhập từ file"
 * ở danh sách khách hàng. Đóng hộp thoại là bỏ luồng đang dở; mở lại bắt đầu từ bước tải file.
 */
export function ContactImportDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const { t } = useTranslation('custList')
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] max-w-5xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('import.title')}</DialogTitle>
          <DialogDescription>
            {t('import.description', { max: formatNumber(MAX_IMPORT_ROWS) })}
          </DialogDescription>
        </DialogHeader>
        {open && <ContactImportFlow onClose={() => onOpenChange(false)} />}
      </DialogContent>
    </Dialog>
  )
}

function ContactImportFlow({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation('custList')
  const perms = useMyPermissions()
  const canImport = perms.data?.has('contacts.import') ?? false

  const [step, setStep] = useState(0)
  const [fileName, setFileName] = useState('')
  const [sheet, setSheet] = useState<SheetData | null>(null)
  const [mapping, setMapping] = useState<TargetField[]>([])
  const [options, setOptions] = useState<ContactImportOptions>(DEFAULT_OPTIONS)
  const [preview, setPreview] = useState<ContactImportResponse | null>(null)
  const [result, setResult] = useState<ContactImportResponse | null>(null)

  const { data: propData } = useCdpProperties()
  const properties = useMemo(() => propData?.properties ?? [], [propData])

  const previewMut = useContactImportPreview()
  const importMut = useContactImport()

  const payload = () => ({
    rows: sheet ? buildImportRows(sheet, mapping) : [],
    options: { ...options, extraTag: options.extraTag?.trim() || undefined },
  })

  const reset = () => {
    setStep(0)
    setFileName('')
    setSheet(null)
    setMapping([])
    setPreview(null)
    setResult(null)
    setOptions(DEFAULT_OPTIONS)
  }

  const onFile = async (file: File) => {
    try {
      const data = await readSpreadsheetFile(file)
      if (!data.headers.length || !data.rows.length) {
        toast.error(t('import.errors.noData'))
        return
      }
      if (data.rows.length > MAX_IMPORT_ROWS) {
        toast.error(t('import.errors.tooManyRows', { count: data.rows.length, rows: formatNumber(data.rows.length), max: formatNumber(MAX_IMPORT_ROWS) }))
        return
      }
      setFileName(file.name)
      setSheet(data)
      setMapping(guessMapping(data.headers, properties))
      setPreview(null)
      setResult(null)
      setStep(1)
    } catch (e) {
      toast.error(e instanceof Error ? e.message : t('import.errors.cannotRead'))
    }
  }

  const runPreview = () => {
    previewMut.mutate(payload(), {
      onSuccess: (r) => {
        setPreview(r)
        setStep(3)
      },
      onError: (e) => toast.error(apiError(e)),
    })
  }

  const runImport = () => {
    if (!preview) return
    const n = preview.summary.created + preview.summary.updated
    if (!confirm(t('import.confirmImport', { count: n, total: formatNumber(n), created: formatNumber(preview.summary.created), updated: formatNumber(preview.summary.updated) }))) return
    importMut.mutate(payload(), {
      onSuccess: (r) => {
        setResult(r)
        setStep(4)
        toast.success(t('import.imported', { created: formatNumber(r.summary.created), updated: formatNumber(r.summary.updated) }))
      },
      onError: (e) => toast.error(apiError(e)),
    })
  }

  /** CSV các dòng lỗi / bỏ qua, kèm dữ liệu gốc để sửa rồi nhập lại. */
  const downloadProblems = (rows: ImportRowResult[], suffix: string) => {
    if (!sheet) return
    const byRow = new Map(sheet.rows.map((r) => [r.rowNumber, r.cells]))
    const lines = [
      [t('import.csv.row'), t('import.csv.result'), t('import.csv.reason'), ...sheet.headers],
      ...rows.map((r) => [
        String(r.rowNumber),
        STATUS_META[r.status].label,
        r.message ?? '',
        ...sheet.headers.map((_, i) => byRow.get(r.rowNumber)?.[i] ?? ''),
      ]),
    ]
    downloadCsv(`${fileName.replace(/\.[^.]+$/, '') || 'khach-hang'}-${suffix}.csv`, toCsv(lines))
  }

  if (perms.isLoading) return <Loading label={t('common:status.loading')} />
  if (!canImport) {
    return (
      <div className="space-y-6">
        <EmptyState
          icon={Lock}
          title={t('import.noPermissionTitle')}
          description={t('import.noPermissionDescription')}
        />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <Stepper step={step} />

      {step === 0 && (
        <UploadStep onFile={onFile} onTemplate={() => downloadCsv('mau-nhap-khach-hang.csv', templateCsv(properties))} />
      )}

      {step === 1 && sheet && (
        <MappingStep
          sheet={sheet}
          fileName={fileName}
          mapping={mapping}
          properties={properties}
          onChange={setMapping}
          onBack={reset}
          onNext={() => setStep(2)}
        />
      )}

      {step === 2 && (
        <OptionsStep
          options={options}
          onChange={setOptions}
          onBack={() => setStep(1)}
          onNext={runPreview}
          loading={previewMut.isPending}
        />
      )}

      {step === 3 && preview && sheet && (
        <PreviewStep
          preview={preview}
          sheet={sheet}
          mapping={mapping}
          onBack={() => setStep(2)}
          onImport={runImport}
          importing={importMut.isPending}
          onDownloadProblems={() => downloadProblems(preview.problems ?? [], 'can-kiem-tra')}
        />
      )}

      {step === 4 && result && (
        <ResultStep
          result={result}
          onDownloadProblems={() =>
            downloadProblems(result.results.filter((r) => r.status === 'error' || r.status === 'skip'), 'loi')
          }
          onAgain={reset}
          onDone={onClose}
        />
      )}
    </div>
  )
}

// ── Thanh bước ───────────────────────────────────────────────────────

function Stepper({ step }: { step: number }) {
  const { t } = useTranslation('custList')
  return (
    <ol className="flex flex-wrap items-center gap-2 text-sm">
      {STEPS.map((stepKey, i) => (
        <li key={stepKey} className="flex items-center gap-2">
          <span
            className={cn(
              'flex h-6 w-6 items-center justify-center rounded-full border text-xs',
              i < step && 'border-primary bg-primary text-primary-foreground',
              i === step && 'border-primary text-primary',
              i > step && 'text-muted-foreground',
            )}
          >
            {i < step ? <Check className="h-3.5 w-3.5" /> : i + 1}
          </span>
          <span className={cn(i === step ? 'font-medium' : 'text-muted-foreground')}>{t(`import.steps.${stepKey}`)}</span>
          {i < STEPS.length - 1 && <span className="mx-1 h-px w-6 bg-border" />}
        </li>
      ))}
    </ol>
  )
}

function StepFooter({ children }: { children: ReactNode }) {
  return <div className="flex flex-wrap items-center justify-between gap-2 pt-2">{children}</div>
}

// ── Bước 1: tải file ─────────────────────────────────────────────────

function UploadStep({ onFile, onTemplate }: { onFile: (f: File) => void; onTemplate: () => void }) {
  const { t } = useTranslation('custList')
  const inputRef = useRef<HTMLInputElement>(null)
  const [dragging, setDragging] = useState(false)

  return (
    <Card>
      <CardContent className="space-y-4 p-6">
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          onDragOver={(e) => {
            e.preventDefault()
            setDragging(true)
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => {
            e.preventDefault()
            setDragging(false)
            const f = e.dataTransfer.files?.[0]
            if (f) onFile(f)
          }}
          className={cn(
            'flex w-full flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed px-6 py-12 text-center transition-colors',
            dragging ? 'border-primary bg-primary/5' : 'border-border hover:border-primary',
          )}
        >
          <Upload className="h-8 w-8 text-muted-foreground" />
          <p className="font-medium">{t('import.upload.drop')}</p>
          <p className="text-sm text-muted-foreground">{t('import.upload.supported')}</p>
        </button>
        <input
          ref={inputRef}
          type="file"
          accept=".csv,.txt,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0]
            if (f) onFile(f)
            e.target.value = ''
          }}
        />
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg bg-muted/50 px-4 py-3 text-sm">
          <div className="flex items-center gap-2 text-muted-foreground">
            <FileSpreadsheet className="h-4 w-4" />
            {t('import.upload.noFile')}
          </div>
          <Button variant="outline" size="sm" onClick={onTemplate}>
            <Download /> {t('import.upload.template')}
          </Button>
        </div>
        <ul className="list-disc space-y-1 pl-5 text-xs text-muted-foreground">
          <li>{t('import.upload.tipPhone')}</li>
          <li>{t('import.upload.tipTags')}</li>
          <li>{t('import.upload.tipStages')}</li>
          <li>{t('import.upload.tipXls')}</li>
        </ul>
      </CardContent>
    </Card>
  )
}

// ── Bước 2: ghép cột ─────────────────────────────────────────────────

function MappingStep({
  sheet,
  fileName,
  mapping,
  properties,
  onChange,
  onBack,
  onNext,
}: {
  sheet: SheetData
  fileName: string
  mapping: TargetField[]
  properties: { fieldKey: string; name: string }[]
  onChange: (m: TargetField[]) => void
  onBack: () => void
  onNext: () => void
}) {
  const { t } = useTranslation('custList')
  const used = new Set(mapping.filter(Boolean))
  const targets: { value: TargetField; label: string }[] = [
    ...BUILTIN_FIELDS.map((f) => ({ value: f.key as TargetField, label: f.label })),
    ...properties.map((p) => ({ value: `prop:${p.fieldKey}` as TargetField, label: t('import.mapping.propertyLabel', { name: p.name }) })),
  ]
  const setAt = (i: number, v: TargetField) => onChange(mapping.map((m, j) => (j === i ? v : m)))
  const ok = hasIdentityColumn(mapping)

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">
          {t('import.mapping.title')} — <span className="font-normal text-muted-foreground">{fileName} · {t('import.mapping.rows', { count: sheet.rows.length, rows: formatNumber(sheet.rows.length) })}</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="overflow-x-auto rounded-lg border">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-left text-xs text-muted-foreground">
              <tr>
                <th className="px-3 py-2 font-medium">{t('import.mapping.colFile')}</th>
                <th className="px-3 py-2 font-medium">{t('import.mapping.colSample')}</th>
                <th className="w-[260px] px-3 py-2 font-medium">{t('import.mapping.colField')}</th>
              </tr>
            </thead>
            <tbody>
              {sheet.headers.map((h, i) => {
                const samples = sheet.rows.slice(0, 3).map((r) => r.cells[i]?.trim()).filter(Boolean)
                return (
                  <tr key={i} className="border-t">
                    <td className="px-3 py-2 font-medium">{h}</td>
                    <td className="max-w-[320px] truncate px-3 py-2 text-muted-foreground">
                      {samples.length ? samples.join(' · ') : '—'}
                    </td>
                    <td className="px-3 py-2">
                      <Select value={mapping[i] || SKIP} onValueChange={(v) => setAt(i, v === SKIP ? '' : (v as TargetField))}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value={SKIP}>{t('import.mapping.skipColumn')}</SelectItem>
                          {targets.map((tg) => (
                            <SelectItem
                              key={tg.value}
                              value={tg.value}
                              // Nhãn cho phép nhiều cột (nối lại); trường khác chỉ một cột.
                              disabled={tg.value !== 'tags' && used.has(tg.value) && mapping[i] !== tg.value}
                            >
                              {tg.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
        {!ok && (
          <p className="flex items-center gap-2 text-sm text-warning">
            <AlertTriangle className="h-4 w-4" /> {t('import.mapping.needIdentity')}
          </p>
        )}
        <StepFooter>
          <Button variant="outline" onClick={onBack}>
            <ArrowLeft /> {t('import.mapping.pickOtherFile')}
          </Button>
          <Button onClick={onNext} disabled={!ok}>
            {t('common:actions.next')} <ArrowRight />
          </Button>
        </StepFooter>
      </CardContent>
    </Card>
  )
}

// ── Bước 3: tuỳ chọn ─────────────────────────────────────────────────

function CheckRow({
  checked,
  onChange,
  label,
  hint,
}: {
  checked: boolean
  onChange: (v: boolean) => void
  label: string
  hint?: string
}) {
  return (
    <label className="flex cursor-pointer items-start gap-3">
      <Checkbox checked={checked} onCheckedChange={(v) => onChange(v === true)} className="mt-0.5" />
      <span>
        <span className="text-sm">{label}</span>
        {hint && <span className="block text-xs text-muted-foreground">{hint}</span>}
      </span>
    </label>
  )
}

function OptionsStep({
  options,
  onChange,
  onBack,
  onNext,
  loading,
}: {
  options: ContactImportOptions
  onChange: (o: ContactImportOptions) => void
  onBack: () => void
  onNext: () => void
  loading: boolean
}) {
  const { t } = useTranslation('custList')
  const team = useTeam()
  const tags = useTags()
  const set = <K extends keyof ContactImportOptions>(k: K, v: ContactImportOptions[K]) => onChange({ ...options, [k]: v })
  const dedupeOn = options.dedupeByPhone || options.dedupeByEmail

  return (
    <Card>
      <CardContent className="space-y-6 p-6">
        <section className="space-y-3">
          <h3 className="text-sm font-medium">{t('import.options.dedupeTitle')}</h3>
          <CheckRow
            checked={options.dedupeByPhone}
            onChange={(v) => set('dedupeByPhone', v)}
            label={t('import.options.byPhone')}
            hint={t('import.options.byPhoneHint')}
          />
          <CheckRow
            checked={options.dedupeByEmail}
            onChange={(v) => set('dedupeByEmail', v)}
            label={t('import.options.byEmail')}
            hint={t('import.options.byEmailHint')}
          />
          {dedupeOn && (
            <div className="space-y-2 pl-7">
              <p className="text-xs text-muted-foreground">{t('import.options.onDuplicate')}</p>
              {(
                [
                  ['skip', t('import.options.skipRow'), t('import.options.skipRowHint')],
                  ['update', t('import.options.updateOld'), t('import.options.updateOldHint')],
                ] as const
              ).map(([v, label, hint]) => (
                <label key={v} className="flex cursor-pointer items-start gap-3">
                  <input
                    type="radio"
                    name="onDuplicate"
                    className="mt-1 accent-primary"
                    checked={options.onDuplicate === v}
                    onChange={() => set('onDuplicate', v)}
                  />
                  <span>
                    <span className="text-sm">{label}</span>
                    <span className="block text-xs text-muted-foreground">{hint}</span>
                  </span>
                </label>
              ))}
            </div>
          )}
          {!dedupeOn && (
            <p className="flex items-center gap-2 text-xs text-warning">
              <AlertTriangle className="h-3.5 w-3.5" /> {t('import.options.allNew')}
            </p>
          )}
        </section>

        <section className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="import-tag">{t('import.options.extraTag')}</Label>
            <Input
              id="import-tag"
              list="import-tag-options"
              placeholder={t('import.options.extraTagPlaceholder')}
              maxLength={50}
              value={options.extraTag ?? ''}
              onChange={(e) => set('extraTag', e.target.value)}
            />
            <datalist id="import-tag-options">
              {(tags.data ?? []).map((tag) => (
                <option key={tag.id} value={tag.name} />
              ))}
            </datalist>
          </div>
          <div className="space-y-2">
            <Label>{t('import.options.assignTo')}</Label>
            <Select
              value={options.assignedUserId ?? NOBODY}
              onValueChange={(v) => set('assignedUserId', v === NOBODY ? undefined : v)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NOBODY}>{t('import.options.noAssign')}</SelectItem>
                {(team.data ?? [])
                  .filter((m) => m.status === 'active')
                  .map((m) => (
                    <SelectItem key={m.id} value={m.id}>
                      {m.fullName || m.email}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">{t('import.options.assignHint')}</p>
          </div>
        </section>

        <section>
          <CheckRow
            checked={options.runAutomation}
            onChange={(v) => set('runAutomation', v)}
            label={t('import.options.runAutomation')}
            hint={t('import.options.runAutomationHint')}
          />
        </section>

        <StepFooter>
          <Button variant="outline" onClick={onBack}>
            <ArrowLeft /> {t('import.mapping.title')}
          </Button>
          <Button onClick={onNext} disabled={loading}>
            {loading ? <RefreshCw className="animate-spin" /> : <ArrowRight />} {t('import.options.check')}
          </Button>
        </StepFooter>
      </CardContent>
    </Card>
  )
}

// ── Bước 4: xem trước ────────────────────────────────────────────────

function SummaryCards({ summary, preview }: { summary: ContactImportResponse['summary']; preview: boolean }) {
  const { t } = useTranslation('custList')
  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
      <StatCard label={preview ? t('import.status.willCreate') : t('import.summary.created')} value={formatNumber(summary.created)} icon={UserPlus} tone="success" />
      <StatCard label={preview ? t('import.status.willUpdate') : t('import.status.updated')} value={formatNumber(summary.updated)} icon={RefreshCw} />
      <StatCard label={t('import.status.skipped')} value={formatNumber(summary.skipped)} icon={SkipForward} tone="warning" />
      <StatCard label={t('import.status.error')} value={formatNumber(summary.errors)} icon={CircleX} tone="destructive" />
    </div>
  )
}

function ResultTable({ rows, preview }: { rows: ImportRowResult[]; preview: boolean }) {
  const { t } = useTranslation('custList')
  return (
    <div className="overflow-x-auto rounded-lg border">
      <table className="w-full text-sm">
        <thead className="bg-muted/50 text-left text-xs text-muted-foreground">
          <tr>
            <th className="w-16 px-3 py-2 font-medium">{t('import.csv.row')}</th>
            <th className="px-3 py-2 font-medium">{t('import.fields.fullName')}</th>
            <th className="px-3 py-2 font-medium">{t('import.fields.phone')}</th>
            <th className="px-3 py-2 font-medium">{t('import.fields.email')}</th>
            <th className="px-3 py-2 font-medium">{t('import.csv.result')}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.rowNumber} className="border-t align-top">
              <td className="px-3 py-2 tabular-nums text-muted-foreground">{r.rowNumber}</td>
              <td className="px-3 py-2">{r.fullName || '—'}</td>
              <td className="px-3 py-2 tabular-nums">{r.phone || '—'}</td>
              <td className="px-3 py-2 text-muted-foreground">{r.email || '—'}</td>
              <td className="px-3 py-2">
                <Badge variant={STATUS_META[r.status].variant}>
                  {preview ? STATUS_META[r.status].preview : STATUS_META[r.status].label}
                </Badge>
                {r.message && <p className="mt-1 text-xs text-muted-foreground">{r.message}</p>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function PreviewStep({
  preview,
  sheet,
  mapping,
  onBack,
  onImport,
  importing,
  onDownloadProblems,
}: {
  preview: ContactImportResponse
  sheet: SheetData
  mapping: TargetField[]
  onBack: () => void
  onImport: () => void
  importing: boolean
  onDownloadProblems: () => void
}) {
  const { t } = useTranslation('custList')
  const { summary } = preview
  const writable = summary.created + summary.updated
  // Dòng lỗi của file thường nằm rải rác — hiện riêng để khỏi phải cuộn tìm.
  const problems = preview.problems ?? []
  const mappedCount = mapping.filter(Boolean).length

  return (
    <div className="space-y-4">
      <SummaryCards summary={summary} preview />
      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            {t('import.preview.title', { count: Math.min(preview.results.length, sheet.rows.length) })}
            <span className="ml-2 text-sm font-normal text-muted-foreground">
              {t('import.preview.subtitle', { total: formatNumber(summary.total), count: mappedCount })}
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ResultTable rows={preview.results} preview />
        </CardContent>
      </Card>

      {problems.length > 0 && (
        <Card>
          <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2">
            <CardTitle className="text-base">{t('import.preview.problemsTitle', { count: problems.length, total: formatNumber(problems.length) })}</CardTitle>
            <Button variant="outline" size="sm" onClick={onDownloadProblems}>
              <Download /> {t('import.preview.downloadList')}
            </Button>
          </CardHeader>
          <CardContent>
            <div className="max-h-[360px] overflow-y-auto">
              <ResultTable rows={problems.slice(0, 200)} preview />
            </div>
            {problems.length > 200 && (
              <p className="mt-2 text-xs text-muted-foreground">{t('import.preview.limit200')}</p>
            )}
          </CardContent>
        </Card>
      )}

      <StepFooter>
        <Button variant="outline" onClick={onBack} disabled={importing}>
          <ArrowLeft /> {t('import.steps.options')}
        </Button>
        <Button onClick={onImport} disabled={importing || writable === 0}>
          {importing ? <RefreshCw className="animate-spin" /> : <Upload />}
          {importing ? t('import.preview.importing') : t('import.preview.importN', { count: writable, total: formatNumber(writable) })}
        </Button>
      </StepFooter>
    </div>
  )
}

// ── Bước 5: kết quả ──────────────────────────────────────────────────

function ResultStep({
  result,
  onDownloadProblems,
  onAgain,
  onDone,
}: {
  result: ContactImportResponse
  onDownloadProblems: () => void
  onAgain: () => void
  onDone: () => void
}) {
  const { t } = useTranslation('custList')
  const problems = result.results.filter((r) => r.status === 'error' || r.status === 'skip')
  return (
    <div className="space-y-4">
      <SummaryCards summary={result.summary} preview={false} />
      {problems.length > 0 ? (
        <Card>
          <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2">
            <CardTitle className="text-base">{t('import.result.notImportedTitle', { count: problems.length, total: formatNumber(problems.length) })}</CardTitle>
            <Button variant="outline" size="sm" onClick={onDownloadProblems}>
              <Download /> {t('import.result.downloadErrors')}
            </Button>
          </CardHeader>
          <CardContent>
            <div className="max-h-[360px] overflow-y-auto">
              <ResultTable rows={problems.slice(0, 200)} preview={false} />
            </div>
            <p className="mt-2 text-xs text-muted-foreground">
              {t('import.result.errorFileHint')}
            </p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="flex items-center gap-2 p-6 text-sm">
            <Check className="h-4 w-4 text-success" /> {t('import.result.allImported')}
          </CardContent>
        </Card>
      )}
      <StepFooter>
        <Button variant="outline" onClick={onAgain}>
          <Upload /> {t('import.result.importAnother')}
        </Button>
        <Button onClick={onDone}>{t('import.result.viewList')}</Button>
      </StepFooter>
    </div>
  )
}
